import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  GroupBadge,
  Input,
  SegmentedControl,
  Skeleton,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute } from '@tanstack/react-router'
import {
  Activity,
  CheckCircle2,
  LogOut,
  Monitor,
  Moon,
  OctagonAlert,
  RefreshCw,
  Sun,
  TriangleAlert,
  UserRound,
  UserRoundX,
} from 'lucide-react'
import { useState } from 'react'

import {
  useExerciseAliases,
  useLiveness,
  useMuscleReference,
  useMyProfile,
  useMyProfileMutations,
  useMySettings,
  useReadiness,
  useSaveMySettings,
} from '@/api/queries'
import type { HealthStatus, ThemeMode } from '@/api/types'
import { ApiError, errorMessage } from '@/api/errors'
import { deleteMyAccount } from '@/api/endpoints/auth'
import * as session from '@/auth/session-store'
import { useCurrentAdmin } from '@/auth/use-session'
import { PageHeader } from '@/components/layout/app-shell'
import { MfaCard } from '@/features/security/mfa-card'
import { PasswordCard } from '@/features/security/password-card'
import { config } from '@/config'
import { MUSCLE_GROUP_KEYS } from '@/api/types'
import { formatNumber } from '@/lib/format'
import { useThemeMode } from '@/lib/theme'
import { notify } from '@/lib/toast'

export const Route = createFileRoute('/_app/impostazioni')({
  component: SettingsPage,
})

const THEME_OPTIONS: { value: ThemeMode; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Chiaro', icon: Sun },
  { value: 'dark', label: 'Scuro', icon: Moon },
  { value: 'system', label: 'Sistema', icon: Monitor },
]

function SettingsPage() {
  const admin = useCurrentAdmin()
  const [themeMode, setThemeMode] = useThemeMode()
  const readiness = useReadiness()
  const liveness = useLiveness()
  const reference = useMuscleReference()
  const settings = useMySettings()
  const saveSettings = useSaveMySettings()
  const [loggingOut, setLoggingOut] = useState(false)
  const [adoptedTheme, setAdoptedTheme] = useState(false)

  /**
   * La preferenza del server si **adotta una volta** all'apertura, non a ogni rilettura.
   *
   * Il locale resta la fonte immediata — la pagina si dipinge subito e non lampeggia —, e
   * il server è la memoria fra un computer e l'altro: chi scegle il tema scuro al banco lo
   * ritrova sul proprio portatile. Adottarla ad ogni fetch riporterebbe indietro una
   * scelta appena fatta, che è il difetto classico di questo tipo di sincronizzazione.
   */
  const remoteTheme = settings.data?.themeMode
  if (!adoptedTheme && remoteTheme !== undefined) {
    setAdoptedTheme(true)
    if (remoteTheme !== themeMode) setThemeMode(remoteTheme)
  }

  function chooseTheme(next: ThemeMode) {
    setThemeMode(next)
    // Il salvataggio è **un effetto collaterale e non un gate**: se `/me/settings` non è
    // servita (il contract la dichiara opzionale in v1) il tema locale funziona comunque,
    // e non si mostra un errore per una preferenza che è già applicata.
    saveSettings.mutate(next)
  }

  const handleLogout = async () => {
    setLoggingOut(true)
    try {
      await session.logout()
      notify({ message: 'Sessione chiusa.', variant: 'success' })
    } finally {
      setLoggingOut(false)
    }
  }

  return (
    <>
      <PageHeader title="Impostazioni" description="Sessione, aspetto e stato del backend." />

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Sessione */}
        <Card>
          <CardHeader>
            <div className="space-y-1">
              <CardTitle>Sessione</CardTitle>
              <CardDescription>Chi sei e come è conservata la sessione.</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="space-y-3 text-sm">
              <Row label="Email" value={admin?.email ?? '—'} />
              <Row label="Ruolo" value={admin?.role ?? '—'} />
              <div>
                <dt className="text-muted-foreground">Id</dt>
                <dd className="mt-0.5 font-mono text-xs break-all">{admin?.id ?? '—'}</dd>
              </div>
            </dl>

            <Alert variant="neutral" title="Dove stanno i token" icon={false}>
              <ul className="space-y-1">
                <li>
                  L'access token sta <strong>solo in memoria</strong> e dura 15 minuti.
                </li>
                <li>
                  Il refresh token viaggia{' '}
                  {config.refreshTransport === 'cookie' ? (
                    <>
                      in un <strong>cookie HttpOnly</strong>, invisibile a JavaScript.
                    </>
                  ) : (
                    <>
                      nel corpo della richiesta e resta in{' '}
                      <code className="font-mono">sessionStorage</code>: muore con la tab, ma è
                      leggibile da un XSS.
                    </>
                  )}
                </li>
              </ul>
            </Alert>

            <Button
              variant="danger-outline"
              loading={loggingOut}
              onClick={() => void handleLogout()}
            >
              <LogOut />
              Esci e revoca la sessione
            </Button>
          </CardContent>
        </Card>

        {/* La password sta **prima** del secondo fattore, e non dopo: è la difesa che
            tutti hanno, e chi arriva qui per cambiarla la cerca per prima. */}
        <PasswordCard />

        {/* Verifica in due passaggi (OPS-04). Sta accanto alla sessione perché è
            la stessa domanda — «quanto è protetto questo accesso» — e non fra le
            preferenze, dove sembrerebbe facoltativa quanto il tema. */}
        <MfaCard />

        {/* Aspetto */}
        <Card>
          <CardHeader>
            <div className="space-y-1">
              <CardTitle>Aspetto</CardTitle>
              <CardDescription>
                Si applica subito qui e si ricorda sull'account:{' '}
                <code className="font-mono text-xs">PUT /me/settings</code>.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            {/* `SegmentedControl` e non tre `role="radio"` fatti a mano: una scelta
                fra tre si naviga con le frecce e ha un solo elemento tabulabile
                (roving tabindex). Dichiararne il ruolo senza implementarne il
                comportamento promette a chi usa la tastiera qualcosa che poi non
                funziona — il design system lo implementa. */}
            <SegmentedControl
              aria-label="Tema"
              fullWidth
              value={themeMode}
              onValueChange={(value) => chooseTheme(value as ThemeMode)}
              options={THEME_OPTIONS.map((option) => ({
                value: option.value,
                label: option.label,
                icon: <option.icon aria-hidden />,
              }))}
            />
          </CardContent>
        </Card>

        {/* Stato del backend */}
        <Card>
          <CardHeader>
            <div className="space-y-1">
              <CardTitle>Stato del backend</CardTitle>
              <CardDescription>
                <code className="font-mono text-xs">GET /health</code> e{' '}
                <code className="font-mono text-xs">GET /health/ready</code>
              </CardDescription>
            </div>
            <Button
              size="sm"
              variant="outline"
              loading={readiness.isFetching || liveness.isFetching}
              onClick={() => {
                void readiness.refetch()
                void liveness.refetch()
              }}
            >
              <RefreshCw />
              Ricontrolla
            </Button>
          </CardHeader>
          <CardContent>
            {/* Le due sonde insieme, perché la coppia è la diagnosi: processo in piedi con
                readiness rossa significa «una dipendenza è giù», entrambe rosse significa
                «il backend non c'è». Separate, la seconda da sola non distingue i due casi
                e la telefonata da fare è diversa. */}
            <div className="border-border mb-3 flex items-center gap-2 border-b pb-3 text-sm">
              <HealthIcon status={liveness.data?.status === 'ok' ? 'ok' : 'unavailable'} />
              <span className="min-w-0 flex-1">
                Processo{' '}
                {liveness.isPending
                  ? '…'
                  : liveness.data?.status === 'ok'
                    ? 'in piedi'
                    : 'non raggiungibile'}
              </span>
              {liveness.data?.version ? (
                <code className="text-muted-foreground font-mono text-xs">
                  v{liveness.data.version}
                </code>
              ) : null}
            </div>

            {readiness.isPending ? (
              <Skeleton className="h-20 w-full" />
            ) : readiness.isError || !readiness.data?.checks ? (
              // Qui si finisce solo se il backend non ha risposto affatto, o ha
              // risposto qualcosa che non è un `Readiness`. Un `503` **non** passa da
              // qui: porta lo stesso schema del `200` e viene mostrato nel dettaglio
              // sotto, che è l'unica forma in cui l'informazione è utile.
              <Alert variant="danger" title="Backend non raggiungibile">
                Nessuna risposta da <code className="font-mono">{config.apiBaseUrl}</code>.
              </Alert>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <HealthIcon status={readiness.data.status} />
                  <span className="text-sm font-medium">
                    {readiness.data.status === 'ok'
                      ? 'Pronto a servire traffico'
                      : readiness.data.status === 'degraded'
                        ? 'Degradato'
                        : 'Non disponibile'}
                  </span>
                  {readiness.data.version ? (
                    <code className="text-muted-foreground ml-auto font-mono text-xs">
                      v{readiness.data.version}
                    </code>
                  ) : null}
                </div>

                <ul className="space-y-1.5">
                  {readiness.data.checks.map((check) => (
                    <li
                      key={check.name}
                      className="bg-surface-alt flex items-center gap-2 rounded-sm px-2.5 py-2 text-sm"
                    >
                      <HealthIcon status={check.status} />
                      <span className="min-w-0 flex-1 truncate">{check.name}</span>
                      {typeof check.latencyMs === 'number' ? (
                        <span className="text-muted-foreground shrink-0 text-xs" data-numeric>
                          {check.latencyMs} ms
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>

                {readiness.data.checks.some((check) => check.message) ? (
                  <ul className="text-muted-foreground space-y-1 text-xs">
                    {readiness.data.checks
                      .filter((check) => check.message)
                      .map((check) => (
                        <li key={check.name}>
                          <span className="font-medium">{check.name}:</span> {check.message}
                        </li>
                      ))}
                  </ul>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contract e vocabolari */}
        <Card>
          <CardHeader>
            <div className="space-y-1">
              <CardTitle>Contract</CardTitle>
              <CardDescription>Cosa parla questo pannello.</CardDescription>
            </div>
            <Activity className="text-muted-foreground size-4 shrink-0" aria-hidden />
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="space-y-3 text-sm">
              <Row label="Versione contract" value={config.contractVersion} />
              <div>
                <dt className="text-muted-foreground">Base URL</dt>
                <dd className="mt-0.5 font-mono text-xs break-all">{config.apiBaseUrl}</dd>
              </div>
              <Row label="Mock" value={config.enableMocks ? 'Attivi (MSW)' : 'Disattivi'} />
            </dl>

            <div className="border-border border-t pt-4">
              <p className="text-muted-foreground mb-2 text-xs font-medium">Gruppi muscolari</p>
              <div className="flex flex-wrap gap-1.5">
                {MUSCLE_GROUP_KEYS.map((group) => (
                  <GroupBadge key={group} group={group} />
                ))}
              </div>
              <p className="text-muted-foreground mt-2 text-xs">
                {reference.data
                  ? `${formatNumber(reference.data.entries.length)} voci nel dizionario muscoli, ${formatNumber(reference.data.canonicalSlugs.length)} slug della mappa corporea.`
                  : 'Chiavi e colori sono contract: la stessa chiave ha lo stesso colore nell’app.'}
              </p>
            </div>

            <div className="border-border border-t pt-4">
              <AliasesPanel />
            </div>
          </CardContent>
        </Card>

        <MyProfileCard />

        <DeleteAccountCard email={admin?.email ?? null} />
      </div>
    </>
  )
}

/**
 * La tabella alias del Coach — `GET /reference/exercise-aliases`.
 *
 * È il rovescio di `unmatchedRateLast7Days` sulla dashboard: quella metrica dice **che**
 * il riconoscimento peggiora, questo elenco è l'unico posto in cui si vede **cosa**
 * riconosce oggi. Sta dietro un pulsante perché sono centinaia di righe e la domanda
 * arriva solo quando il numero sale.
 */
function AliasesPanel() {
  const [open, setOpen] = useState(false)
  const aliases = useExerciseAliases(open)
  const rows = aliases.data?.data ?? []

  return (
    <>
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-muted-foreground text-xs font-medium">Alias del Coach</p>
        <Button size="sm" variant="ghost" onClick={() => setOpen((value) => !value)}>
          {open ? 'Nascondi' : 'Mostra'}
        </Button>
      </div>
      {!open ? (
        <p className="text-muted-foreground text-xs">
          I nomi che i personal trainer usano davvero — «panca piana» → «Barbell Bench Press».
          Quando <code className="font-mono">unmatchedRateLast7Days</code> sale, è qui che si
          guarda.
        </p>
      ) : aliases.isPending ? (
        <Skeleton className="h-16 w-full" />
      ) : aliases.isError ? (
        <Alert variant="danger">{errorMessage(aliases.error)}</Alert>
      ) : (
        <>
          <p className="text-muted-foreground mb-2 text-xs">
            {formatNumber(rows.length)} alias.{' '}
            {rows.some((row) => row.exerciseId == null)
              ? 'Quelli in grigio puntano a un nome che non è (più) in catalogo: sono i primi a produrre un `unmatched`.'
              : 'Tutti puntano a un esercizio esistente.'}
          </p>
          <ul className="max-h-64 space-y-1 overflow-y-auto text-xs">
            {rows.map((row) => (
              <li
                key={`${row.alias}-${row.exerciseName}`}
                className={row.exerciseId == null ? 'text-muted-foreground' : undefined}
              >
                <span className="font-medium">{row.alias}</span> → {row.exerciseName}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  )
}

/**
 * Il proprio profilo — `GET`/`PUT`/`PATCH /me/profile`.
 *
 * Il nome non è cosmetico: compare accanto a ogni azione nel registro di audit, e chi lo
 * legge un anno dopo cerca una persona da chiamare. Fino a `draft.86` il pannello lo
 * leggeva da `GET /me` e non lo poteva correggere — un operatore registrato con il nome
 * sbagliato restava così per sempre.
 *
 * `404` è una risposta **normale**: un account nato da un invito senza dati non ha ancora
 * un profilo, e in quel caso si salva con `PUT` (crea) invece che con `PATCH` (corregge).
 */
function MyProfileCard() {
  const profile = useMyProfile()
  const { save } = useMyProfileMutations()
  const [name, setName] = useState('')
  const [adopted, setAdopted] = useState(false)

  const loaded = profile.data?.data
  if (!adopted && loaded !== undefined) {
    setAdopted(true)
    setName(loaded.name)
  }

  const missing =
    profile.isError && profile.error instanceof ApiError && profile.error.status === 404
  const dirty = loaded === undefined ? name.trim() !== '' : name.trim() !== loaded.name

  return (
    <Card>
      <CardHeader>
        <div className="space-y-1">
          <CardTitle>Il tuo profilo</CardTitle>
          <CardDescription>
            Il nome con cui compari nel registro delle azioni, non solo qui.
          </CardDescription>
        </div>
        <UserRound className="text-muted-foreground size-4 shrink-0" aria-hidden />
      </CardHeader>
      <CardContent className="space-y-3">
        {profile.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <>
            {missing ? (
              <Alert variant="neutral">
                Non hai ancora un profilo: succede a chi è entrato da un invito. Scrivi il tuo nome
                e viene creato.
              </Alert>
            ) : null}
            {profile.isError && !missing ? (
              <Alert variant="danger">{errorMessage(profile.error)}</Alert>
            ) : null}

            <Field label="Nome" required>
              {(props) => (
                <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />
              )}
            </Field>

            {save.isError ? <Alert variant="danger">{errorMessage(save.error)}</Alert> : null}

            <Button
              size="sm"
              disabled={!dirty || name.trim() === ''}
              loading={save.isPending}
              onClick={() =>
                save.mutate(
                  {
                    input: { name: name.trim() },
                    etag: profile.data?.etag ?? null,
                    exists: loaded !== undefined,
                  },
                  {
                    onSuccess: () => notify({ message: 'Profilo aggiornato.', variant: 'success' }),
                  },
                )
              }
            >
              Salva
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * `DELETE /me` — cancellare **il proprio** account.
 *
 * ⚠️ Non è la rimozione di un operatore da un'organizzazione: quella la fa il titolare da
 * «Operatori», lascia l'account in piedi ed è reversibile con un nuovo invito. Questa la
 * fa la persona su sé stessa, cancella tutto ciò che vi è collegato e non torna indietro.
 * Il testo lo dice prima del pulsante, perché la distinzione è invisibile dal nome.
 *
 * La password è la conferma richiesta dal contract, e non un vezzo dell'interfaccia: è
 * l'unico modo di sapere che sta cancellando la persona e non una sessione lasciata aperta
 * su un computer del banco. È lo stesso principio del cognome sull'anonimizzazione di
 * un'anagrafica — si riscrive un dato che si ha, e nessun altro ha.
 */
function DeleteAccountCard({ email }: { email: string | null }) {
  const [open, setOpen] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      await deleteMyAccount(password)
      // Nessun `toast` di successo: la sessione non esiste più, e la pagina che lo
      // mostrerebbe non c'è. Si esce, che è l'unico esito coerente.
      await session.logout()
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="border-danger/40">
      <CardHeader>
        <div className="space-y-1">
          <CardTitle>Elimina il tuo account</CardTitle>
          <CardDescription>Definitivo, e riguarda te — non la palestra.</CardDescription>
        </div>
        <UserRoundX className="text-danger-content size-4 shrink-0" aria-hidden />
      </CardHeader>
      <CardContent className="space-y-3">
        <Alert variant="warning" title="Cosa fa, e cosa non fa" icon={false}>
          <ul className="space-y-1">
            <li>
              Cancella <strong>{email ?? 'questo account'}</strong> e tutto ciò che vi è collegato.
              Non si annulla.
            </li>
            <li>
              <strong>Non</strong> è «uscire dalla palestra»: per togliere un operatore da
              un'organizzazione c'è «Operatori», e l'account resta.
            </li>
          </ul>
        </Alert>

        {!open ? (
          <Button variant="danger-outline" size="sm" onClick={() => setOpen(true)}>
            Voglio eliminare l'account
          </Button>
        ) : (
          <>
            <Field label="La tua password" required hint="Serve come conferma.">
              {(props) => (
                <Input
                  {...props}
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
            </Field>
            {error === null ? null : <Alert variant="danger">{error}</Alert>}
            <div className="flex gap-2">
              <Button
                variant="danger"
                size="sm"
                disabled={password === ''}
                loading={busy}
                onClick={() => void submit()}
              >
                Elimina definitivamente
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setOpen(false)
                  setPassword('')
                  setError(null)
                }}
              >
                Annulla
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function HealthIcon({ status }: { status: HealthStatus }) {
  if (status === 'ok') {
    return <CheckCircle2 className="text-success-content size-4 shrink-0" aria-label="ok" />
  }
  if (status === 'degraded') {
    return <TriangleAlert className="text-warning-content size-4 shrink-0" aria-label="degradato" />
  }
  return (
    <OctagonAlert className="text-danger-content size-4 shrink-0" aria-label="non disponibile" />
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground shrink-0">{label}</dt>
      <dd className="truncate text-right font-medium">{value}</dd>
    </div>
  )
}
